import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LuFileQuestion } from "react-icons/lu";
import { MdDeleteOutline } from "react-icons/md";

import AssetThumbnail from "@/components/AssetThumbnail";
import AssetMediaTypeBadge from "@/components/ui/AssetMediaTypeBadge";
import { ASSETS_LIST_BOX_SIZE_CLASS } from "./constants";

export default function AssetsListBox({
  entryKey,
  assetId,
  asset,
  isPreviewing = false,
  onRemoveEntry,
  onOpenEntryPreview,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entryKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Marks the entry AssetsListAssetPreview is showing.
  //
  // An OUTLINE pulled inside by a negative offset, not a ring. A ring is a
  // box-shadow: an outer one is eaten by the row's own overflow, and an inset
  // one paints *under* the thumbnail filling the box. An outline paints over
  // its descendants, and at -3px it sits inside the box where nothing clips
  // it. (Note `ring-inset` does not exist in Tailwind v4 at all — it is
  // `inset-ring-*` — so the v3 spelling fails silently.)
  const previewingClass = isPreviewing
    ? "outline-3 outline-primary -outline-offset-3"
    : "";

  const handlePreviewClick = (event) => {
    event.stopPropagation();
    onOpenEntryPreview({ entryKey });
  };

  const handleRemoveClick = (event) => {
    event.stopPropagation();
    onRemoveEntry({ entryKey });
  };

  return (
    <div
      id={`AssetsListBox-${entryKey}`}
      data-asset-id={assetId}
      ref={setNodeRef}
      style={style}
      className={`AssetsListBox group relative shrink-0 rounded-md border border-secondary bg-background overflow-hidden cursor-grab select-none ${ASSETS_LIST_BOX_SIZE_CLASS} ${previewingClass} ${isDragging ? "opacity-50" : ""}`}
      title={asset ? asset.name : "Asset unavailable"}
      {...attributes}
      {...listeners}>
      {asset ? (
        <>
          <div className="w-full h-full" onClick={handlePreviewClick}>
            <AssetThumbnail asset={asset} />
          </div>
          {/* `sm` because the box scales with the strip and is 48px at its
              smallest — the grid tile's badge swamps it at that size. */}
          <AssetMediaTypeBadge asset={asset} size="sm" />
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-card">
          <LuFileQuestion className="w-1/2 h-1/2 text-secondary-foreground/30" />
        </div>
      )}

      <button
        id={`AssetsListBox__remove-${entryKey}`}
        type="button"
        title="Remove from list"
        // dnd-kit starts its drag on pointerdown; without this the click never lands.
        onPointerDown={(event) => event.stopPropagation()}
        onClick={handleRemoveClick}
        className="AssetsListBox__remove absolute top-0.5 right-0.5 p-1 rounded-sm bg-background/90 text-secondary-foreground/80 hover:text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
        <MdDeleteOutline className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
